import * as React from 'react';
import { useHistory } from 'react-router-dom';
import { useObservable } from 'react-use-observable';

import { useQuery } from '@tanstack/react-query';
import { IS_PROD } from 'settings';
import ModalBelt from 'v2/components/Shared/ModalBelt';
import eduzzAccountsService from 'v2/services/eduzzAccount';
import tokenService from 'v2/services/token';

import type { QuickStartProps } from '.';

export function WithQualificationValidation<T extends QuickStartProps>(Component: React.ComponentType<T>) {
  const ValidatedComponent = (props: T) => {
    const [user] = useObservable(() => eduzzAccountsService.getUser(), []);
    const [accountsId] = useObservable(() => tokenService.getAccountsId(), []);
    const [qualificationKey] = useObservable(() => tokenService.getQualificationKey(), []);
    const hasAcceptedTerms = !!user?.acceptence_terms;
    const hasRedirectQuery = useQuery({
      queryKey: ['qualificationRedirect'],
      queryFn: () => eduzzAccountsService.checkQualificationRedirect(accountsId),
      enabled: !!accountsId && hasAcceptedTerms
    });
    const history = useHistory();

    const redirectUserToQualification = React.useCallback((accountId: string, qualificationKey: string) => {
      const params = `?user_id=${accountId}&type=qualificacao-inicial&utm_source=orbita&key=${qualificationKey}`;
      const url = IS_PROD ? `https://qualifier.eduzz.com/${params}` : `https://qualifier.testzz.ninja/${params}`;
      window.location.href = url;
    }, []);

    React.useEffect(() => {
      if (!user) return;
      const isProducerOrCoProducerOrAffiliate = user.producer || user.coproducer || user.affiliate;
      if (user.acceptence_terms && isProducerOrCoProducerOrAffiliate) return history.push('/producer/dashboard');

      if (!hasAcceptedTerms) return;
      if (user.is_support || hasRedirectQuery.isLoading) return;
      const shouldRedirect = hasRedirectQuery.data && !hasRedirectQuery.data.hasRedirect;

      if (shouldRedirect) {
        redirectUserToQualification(accountsId, qualificationKey);
      }
    }, [redirectUserToQualification, accountsId, qualificationKey, user, history, hasRedirectQuery, hasAcceptedTerms]);

    if (!user) return null;

    return (
      <>
        <ModalBelt />
        <Component user={user} {...props} />
      </>
    );
  };

  return ValidatedComponent;
}
